/** Workspace-контекст: активная локация (выбор в шапке).
 *
 *  Хранит выбор в localStorage, чтобы переживал перезагрузку.
 */
import { createContext, createMemo, createSignal, useContext, type ParentComponent } from 'solid-js'
import { useAuth } from '~/lib/auth'
import type { Location } from '~/types'

const STORAGE_KEY = 'ritm.active_location_id'

interface WorkspaceContextValue {
  activeLocation: () => Location | null
  setActiveLocationId: (id: number | null) => void
  availableLocations: () => Location[]
}

const WorkspaceContext = createContext<WorkspaceContextValue>()

export const WorkspaceProvider: ParentComponent = (props) => {
  const { meQuery } = useAuth()

  const initial = (() => {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const id = parseInt(raw, 10)
    return Number.isNaN(id) ? null : id
  })()

  const [activeLocationId, setActiveLocationIdSignal] = createSignal<number | null>(initial)

  const setActiveLocationId = (id: number | null) => {
    if (id === null) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, String(id))
    setActiveLocationIdSignal(id)
  }

  const availableLocations = createMemo<Location[]>(() => meQuery.data?.available_locations ?? [])

  const activeLocation = createMemo<Location | null>(() => {
    const id = activeLocationId()
    if (id === null) return null
    const found = availableLocations().find((l) => l.id === id)
    if (!found && availableLocations().length > 0) {
      // Локация больше недоступна — сбрасываем.
      setActiveLocationId(null)
      return null
    }
    return found ?? null
  })

  return (
    <WorkspaceContext.Provider value={{ activeLocation, setActiveLocationId, availableLocations }}>
      {props.children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider')
  return ctx
}
